import {
  ConditionRenderFragment,
  GoalRenderFragment,
  ObservationRenderFragment,
} from '@eluve/fhir-renderer';
import { graphql } from '@eluve/graphql-fhir';

/**
 * FHIR Patient lookup by Eluve patient identifier (the Hasura patient id the
 * profile route carries). Mirrors the goals card's query — the care plans
 * section keys everything off the FHIR id.
 */
export const FHIR_PATIENT_ID_QUERY = graphql(`
  query FhirCarePlansPatientId($identifier: String!) {
    PatientList(identifier: $identifier, _count: 1) {
      id
    }
  }
`);

/**
 * The patient's care plans, one bounded page per explicit loadMore. There is
 * no CarePlanRender fragment (CarePlan falls through to the renderer's
 * GenericView), so the selection is owned here: everything the plan-shapes
 * decoders read off the CarePlan itself — status/intent (active vs draft),
 * period + activity phase spine (scheduledPeriod, phase codings, stick refs),
 * the session-frequency / visit-override / plan-shape extensions, and the
 * goal/addresses references the card cross-links to the Goals and Conditions
 * sections.
 */
export const PATIENT_CARE_PLANS_QUERY = graphql(`
  query FhirPatientCarePlans($patientRef: String!, $offset: Int) {
    CarePlanConnection(
      patient: $patientRef
      _count: 100
      _offset: $offset
      _sort: "-_lastUpdated"
    ) {
      count
      offset
      edges {
        resource {
          resourceType
          id
          meta {
            versionId
            lastUpdated
            tag {
              system
              code
            }
          }
          status
          intent
          title
          created
          instantiatesCanonical
          subject {
            reference
            type
            display
          }
          author {
            reference
            type
            display
          }
          period {
            start
            end
          }
          addresses {
            reference
            type
            display
          }
          goal {
            reference
            type
            display
          }
          activity {
            extension {
              url
              valueCoding {
                system
                code
                display
              }
              valueReference {
                reference
              }
            }
            detail {
              status
              description
              code {
                text
                coding {
                  system
                  code
                  display
                }
              }
              scheduledPeriod {
                start
                end
              }
            }
          }
          extension {
            url
            valueCode
            extension {
              url
              valueInteger
              valueDate
              valueTiming {
                repeat {
                  frequency
                  period
                  periodUnit
                  boundsDuration {
                    value
                    unit
                    system
                    code
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

/**
 * The goal window `CarePlan.goal` references resolve against — the same
 * selection (and so the same server data) as the Goals card's read, keeping
 * the two sections' rows telling one story. A linked goal outside the
 * loaded window degrades to its reference display — never a fetch-all.
 */
export const PLAN_GOALS_QUERY = graphql(
  `
    query FhirCarePlanGoals($patientRef: String!, $offset: Int) {
      GoalConnection(
        patient: $patientRef
        _count: 100
        _offset: $offset
        _sort: "-_lastUpdated"
      ) {
        count
        offset
        edges {
          resource {
            ...GoalRender
          }
        }
      }
    }
  `,
  [GoalRenderFragment],
);

/**
 * The condition window `CarePlan.addresses` references resolve against —
 * the same selection (and so the same server data) as the Conditions card's
 * read, keeping the two sections' rows telling one story. A treated
 * condition outside the loaded window degrades to its reference display —
 * never a fetch-all.
 */
export const PLAN_CONDITIONS_QUERY = graphql(
  `
    query FhirCarePlanConditions($patientRef: String!, $offset: Int) {
      ConditionConnection(
        patient: $patientRef
        _count: 100
        _offset: $offset
        _sort: "-recorded-date"
      ) {
        count
        offset
        edges {
          resource {
            ...ConditionRender
          }
        }
      }
    }
  `,
  [ConditionRenderFragment],
);

/**
 * The observation window the expanded card's goal rows resolve tracked
 * series from (sparkline + "now" value) — the same shape as the Goals
 * card's read. A goal whose addressed observation falls outside the loaded
 * window simply renders without a trend — never a fetch-all.
 */
export const PLAN_TRACKED_OBSERVATIONS_QUERY = graphql(
  `
    query FhirCarePlanTrackedObservations($patientRef: String!, $offset: Int) {
      ObservationConnection(
        patient: $patientRef
        _count: 100
        _offset: $offset
        _sort: "-date"
      ) {
        count
        offset
        edges {
          resource {
            ...ObservationRender
          }
        }
      }
    }
  `,
  [ObservationRenderFragment],
);

/**
 * The plan-linked ServiceRequests the card resolves phase progress from:
 * the per-phase spine sticks (intent `plan`, the searchable phase status +
 * visit targets) and the prescribed leaf activities (intent `order`), both
 * carrying `basedOn` → their CarePlan. One bounded patient-wide window,
 * grouped per plan client-side — a plan whose requests fall outside the
 * loaded window degrades to its CarePlan-only summary, never a fetch-all.
 */
export const PLAN_SERVICE_REQUESTS_QUERY = graphql(`
  query FhirCarePlanServiceRequests($patientRef: String!, $offset: Int) {
    ServiceRequestConnection(
      patient: $patientRef
      _count: 100
      _offset: $offset
      _sort: "-_lastUpdated"
    ) {
      count
      offset
      edges {
        resource {
          resourceType
          id
          status
          intent
          authoredOn
          instantiatesCanonical
          basedOn {
            reference
          }
          category {
            coding {
              system
              code
              display
            }
          }
          code {
            text
            coding {
              system
              code
              display
            }
          }
          quantityQuantity {
            value
            unit
          }
          occurrencePeriod {
            start
            end
          }
          occurrenceTiming {
            repeat {
              frequency
              period
              periodUnit
              boundsDuration {
                value
                unit
                system
                code
              }
            }
          }
          patientInstruction
          note {
            text
          }
          extension {
            url
            valueCoding {
              system
              code
              display
            }
            extension {
              url
              valueInteger
              valueString
            }
          }
        }
      }
    }
  }
`);
